import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { KoobTableToolbarProvider } from './KoobTableToolbar';

const layoutVariants = cva('w-full mx-auto flex flex-col', {
  variants: {
    // narrow = small CRUD/settings tables, wide = standard admin lists,
    // full = dense data pages (bookings, folders)
    width: {
      full: '',
      wide: 'max-w-7xl',
      narrow: 'max-w-3xl',
    },
  },
  defaultVariants: {
    width: 'full',
  },
});

type KoobListPageLayoutProps = {
  /** Search input, at the standard width in the table's toolbar. */
  search?: React.ReactNode;
  /** Inline filter controls, rendered next to the search input. */
  filters?: React.ReactNode;
  /** Right-aligned toolbar extras: export, bulk edit, locale switcher, … */
  toolbarActions?: React.ReactNode;
  /** The card's foot, flush with its bottom edge: an embedded KoobPagination. */
  footer?: React.ReactNode;
  /** Drops the card for a list that sits inside another card, which would
   *  otherwise draw a second border (as KoobTable's `embedded`). */
  embedded?: boolean;
  children: React.ReactNode;
} & VariantProps<typeof layoutVariants>;

// The body of a list page: its search, filters and extras sit on top of the
// table, inside its card (the first KoobDataTable inside takes them); with no
// table on screen, they head a card of the same look around the content. The title, count and
// actions go in the page's KoobPageHeader.
const KoobListPageLayout = React.forwardRef<
  HTMLDivElement,
  KoobListPageLayoutProps
>(
  (
    { search, filters, toolbarActions, footer, embedded, width, children },
    ref,
  ) => (
    <div ref={ref} className={layoutVariants({ width })}>
      <KoobTableToolbarProvider
        toolbar={{ search, filters, actions: toolbarActions }}
        footer={footer}
        embedded={embedded}
      >
        {children}
      </KoobTableToolbarProvider>
    </div>
  ),
);

KoobListPageLayout.displayName = 'KoobListPageLayout';

export default KoobListPageLayout;
